import React from "react";
import axios from "axios";
import crashState from "./crashState";
import { getAuthToken } from "../Auth";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import { crashDetails } from "@mrwa/crashmap/esriControls/crashmapTools/query/crashDetails";
import filterManager from "@mrwa/crashmap/esriControls/crashmapTools/query/filterManager";
import CrashFilterPanel from "@mrwa/crashmap/esriControls/crashmapTools/query/CrashFilterPanel";
import crashResultMarkup from "@mrwa/crashmap/esriControls/crashmapTools/query/crashResultMarkup";
import crashDetailsDisplayManager from "@mrwa/crashmap/esriControls/crashmapTools/query/crashDetailsDisplayManager";
import CrashResultsButton from "@mrwa/crashmap/esriControls/crashmapTools/query/CrashResultsButton";
import AddCollisionDiagramTool from "@mrwa/crashmap/esriControls/crashmapTools/collisionDiagrams/AddCollisionDiagram";
import { emptyStateObject } from "@mrwa/crashmap/esriControls/crashmapTools/crashDetailsState";

import { ReactComponent as ACDTButtonIconComponent } from "@mrwa/rime-replacement/esriControls/tools/point_select.svg";
import { ReactComponent as AddCDButtonIconComponent } from "@mrwa/crashmap/esriControls/crashmapTools/collisionDiagrams/callout_add.svg";
import { ReactComponent as RemoveCDButtonIconComponent } from "@mrwa/crashmap/esriControls/crashmapTools/collisionDiagrams/callout_remove.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPercentage } from "@fortawesome/free-solid-svg-icons";

import "@mrwa/rime-replacement/esriControls/general/panel.css";
import "@mrwa/crashmap/esriControls/crashmapTools/query/crashDetailsPopup.css";
import "@mrwa/crashmap/esriControls/crashmapTools/query/crashFilterPanel.css";
import "./crashData.css";

/**
 * Shows the crash results associated with the loaded video speed survey and optionally allows filtering
 */
export default class CrashData extends React.Component {
  constructor(props) {
    super(props);

    this.filterManager = null;
    this.crashDetailsDisplayManager = null;
    this.crashResultMarkup = new crashResultMarkup(
      this.props.mapSettings.severityDisplaySettings
    );

    this.state = {
      filters: null,
      crashes: null,
      filteredCrashes: null,
    };

    this.gpsFileChanged = this.gpsFileChanged.bind(this);
    this.getAuthorizationHeader = this.getAuthorizationHeader.bind(this);
    this.handleFilterUpdated = this.handleFilterUpdated.bind(this);
    this.updateFilteredCrashes = this.updateFilteredCrashes.bind(this);
    this.showCollisionDiagrams = this.showCollisionDiagrams.bind(this);
    this.handleShowQuerySummary = this.showQuerySummary.bind(this, null);
  }

  //create an authorisation header object for queries to the crash data API
  async getAuthorizationHeader() {
    return {
      headers: {
        Authorization: `Bearer ${await getAuthToken(this.props.accountId)}`,
      },
    };
  }

  async componentDidMount() {
    const filterQuery = await axios.get(
      `${this.props.mapSettings.crashDataApiUrl}/CrashFilters`,
      await this.getAuthorizationHeader()
    );
    this.searchResultFilters = JSON.parse(filterQuery.data);

    this.filterManager = new filterManager(
      this.searchResultFilters.Groups.filter((group) =>
        this.props.mapSettings.crashFilterGroupLookups.includes(
          group.LookupDomain
        )
      )
    );
    this.crashDetailsDisplayManager = new crashDetailsDisplayManager(
      mapHelper.mapView,
      this.props.mapSettings.severityDisplaySettings,
      this.showCrashPopupContent.bind(this),
      () => this.state.filteredCrashes,
      crashState.fetchCrashVehicleDetails.bind(crashState)
    );
    this.setState({ filters: this.filterManager.unfiltered });
    this.props.playerContext.onGpsFileChanged(this.gpsFileChanged);
    this.props.playerContext.onContextStateChanged(this.hideCrashDisplays);
    await this.crashResultMarkup.initialise(mapHelper.mapView);
  }

  componentDidUpdate(prevProps) {
    if (this.props.playerContext !== prevProps.playerContext) {
      this.props.playerContext.onGpsFileChanged(this.gpsFileChanged);
      this.props.playerContext.onContextStateChanged(this.hideCrashDisplays);
    }
  }

  /**
   * Reacts to a gps file change by loading all the crash data based on the route followed in the current video
   * @param {object} gpsInformation contains all gps rows and also the current gps row, in addition to the buffered line that marks the route
   */
  async gpsFileChanged(gpsInformation) {
    const bufferedGeometry = await gpsInformation.bufferedGeometry;
    const projectedGeometry = await mapHelper.projectToView(bufferedGeometry, {
      wkid: 4283,
    });
    //query the crashes within the buffered geometry representing the currently loaded run
    const crashQuery = await axios.post(
      `${this.props.mapSettings.crashDataApiUrl}/QueryCrashes`,
      {
        areaParams: projectedGeometry,
        extent: projectedGeometry.extent,
      },
      await this.getAuthorizationHeader()
    );

    if (crashQuery.data && crashQuery.data.length > 0) {
      //generate geometry and projected geometries required for crashResultMarkup
      const queryResults = await Promise.all(
        crashQuery.data.map(async (result) => new crashDetails(result))
      );
      const sortedCrashes = crashDetails.sortBySlk(queryResults);
      this.crashResultMarkup.clearResultsMarkup();
      this.crashResultMarkup.addResultMarkers(sortedCrashes);
      this.setState({
        crashes: sortedCrashes,
        filteredCrashes: sortedCrashes,
      });
    }
  }

  showCrashPopupContent(content) {
    content.crashDetails.ProjectedGeometry.then((geometry) =>
      mapHelper.mapView.goTo({ center: geometry })
    );
    crashState.currentCrashPopupContent = {
      hasContent: true,
      content,
    };
  }

  handleFilterUpdated(updatedFilters) {
    this.setState({
      filters: updatedFilters,
    });
    this.updateFilteredCrashes(updatedFilters);
  }

  updateFilteredCrashes(updatedFilters) {
    const filteredCrashes = filterManager.filterCrashes(
      this.state.crashes,
      updatedFilters
    );
    this.crashResultMarkup.clearResultsMarkup();
    this.crashResultMarkup.addResultMarkers(filteredCrashes);
    this.setState({ filteredCrashes });

    if (
      crashState.currentQuerySummaryPopupContent &&
      crashState.currentQuerySummaryPopupContent.hasContent
    ) {
      this.showQuerySummary(filteredCrashes);
    }

    if (
      crashState.crashVehicleDetails &&
      crashState.crashVehicleDetails.length === 0
    ) {
      this.crashDetailsDisplayManager.cacheVehicleDetails(null);
    }

    if (crashState.collisionDiagramFeatures) {
      crashState.collisionDiagramFeatures =
        crashState.collisionDiagramFeatures.filter((crash) =>
          filteredCrashes.includes(crash)
        );
    }
  }

  async showCollisionDiagrams() {
    const visibleCrashes = [];
    for (let crash of this.state.filteredCrashes) {
      if (mapHelper.mapView.extent.contains(await crash.ProjectedGeometry))
        visibleCrashes.push(crash);
    }
    crashState.collisionDiagramFeatures = visibleCrashes;
  }

  hideCrashDisplays() {
    crashState.collisionDiagramFeatures = [];
    crashState.currentQuerySummaryPopupContent = emptyStateObject;
    crashState.currentCrashPopupContent = emptyStateObject;
  }

  showQuerySummary(crashes) {
    crashState.currentQuerySummaryPopupContent = {
      hasContent: true,
      content: {
        crashes: crashes || this.state.filteredCrashes,
        searchResultFilters: this.searchResultFilters,
        displaySettings: this.props.mapSettings.severityDisplaySettings,
      },
    };
  }

  render() {
    if (this.state.crashes && this.filterManager) {
      return (
        <>
          <div className="filterControlPanel">
            <CrashFilterPanel
              originalFilters={this.filterManager.unfiltered}
              filters={this.state.filters}
              onFiltersChanged={this.handleFilterUpdated}
              itemKey={filterManager.itemKey}
            />
          </div>
          <div className="crashButtonsToolbar toolBar">
            <div>
              <AddCollisionDiagramTool
                view={mapHelper.mapView}
                crashes={this.state.filteredCrashes}
                crashDetailsState={crashState}
                buttonIconComponent={ACDTButtonIconComponent}
              />
            </div>
            <div>
              <CrashResultsButton
                title="Remove Collision Diagrams"
                onClick={this.hideCrashDisplays}
                icon={<RemoveCDButtonIconComponent className="buttonIcon" />}
              />
            </div>
            <div>
              <CrashResultsButton
                title="Show All Collision Diagrams"
                onClick={this.showCollisionDiagrams}
                icon={<AddCDButtonIconComponent className="buttonIcon" />}
              />
            </div>
            <div>
              <CrashResultsButton
                title="View Query Summary"
                onClick={this.handleShowQuerySummary}
                icon={
                  <FontAwesomeIcon
                    icon={faPercentage}
                    className="buttonIcon"
                    size="lg"
                  />
                }
              />
            </div>
          </div>
        </>
      );
    } else return null;
  }
}
